import React from 'react';
import { useTranslation } from 'react-i18next';
import U2000BasicDialog from '../../../../u2000Core/components/dialog/U2000BasicDialog';
import { NS } from '../../../U3141';
import type { U3141PlanUploadProgressUiState } from '../../../tools/u3141PlanUploadProgressState';

const closeButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-xs transition-all duration-200
  hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 hover:shadow-md
  focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2`;

export interface U3141PlanUploadProgressDialogProps {
  state: U3141PlanUploadProgressUiState;
  onClose: () => void;
}

export const U3141PlanUploadProgressDialog: React.FC<U3141PlanUploadProgressDialogProps> = ({
  state,
  onClose,
}) => {
  const { t } = useTranslation();
  if (!state.open) {
    return null;
  }

  const safePercent = Math.max(0, Math.min(100, state.percent));
  const hasCount = state.total > 0;
  const isTerminal = state.status === 'success' || state.status === 'partial' || state.status === 'failed';

  return (
    <U2000BasicDialog
      isOpen={state.open}
      onClose={onClose}
      title={t('dlgPlanUploadProgressTitle', { ns: NS })}
      width="medium"
      height="max-h-[90vh]"
    >
      <div className="flex flex-col h-full focus:outline-hidden">
        <section className="flex-1 px-6 py-4 overflow-y-auto min-h-0 space-y-4">
          <p className="text-sm text-gray-700">{t('dlgPlanUploadProgressIntro', { ns: NS })}</p>
          {state.zipArchiveCount > 0 ? (
            <p className="text-xs text-gray-600">
              {t('dlgPlanUploadProgressZipCount', { ns: NS, count: state.zipArchiveCount })}
            </p>
          ) : null}
          <div className="h-2 w-full rounded-sm bg-gray-200">
            <div
              className="h-2 rounded-sm bg-green-600 transition-all duration-300"
              style={{ width: `${safePercent}%` }}
            />
          </div>
          <p className="text-xs text-gray-600">
            {t('dlgPlanUploadProgressPercent', { ns: NS, percent: safePercent })}
          </p>
          {hasCount ? (
            <p className="text-sm font-medium text-gray-900">
              {t('dlgPlanUploadProgressCount', { ns: NS, current: state.current, total: state.total })}
            </p>
          ) : state.status === 'running' ? (
            <p className="text-sm text-gray-600">{t('dlgPlanUploadProgressNoPlans', { ns: NS })}</p>
          ) : null}
          {state.fileLabel && state.status === 'running' ? (
            <p className="text-sm text-gray-700">
              {t('dlgPlanUploadProgressCurrentFile', { ns: NS, file: state.fileLabel })}
            </p>
          ) : null}
          {state.status === 'success' ? (
            <p className="text-sm text-green-800">
              {t('dlgPlanUploadProgressDoneSuccess', {
                ns: NS,
                successCount: state.doneSuccessCount ?? 0,
              })}
            </p>
          ) : null}
          {state.status === 'partial' ? (
            <p className="text-sm text-amber-800">
              {t('dlgPlanUploadProgressDonePartial', {
                ns: NS,
                successCount: state.doneSuccessCount ?? 0,
                errorCount: state.doneErrorCount ?? 0,
              })}
            </p>
          ) : null}
          {state.status === 'failed' && state.globalError ? (
            <p className="text-sm text-red-700">{state.globalError}</p>
          ) : null}
          {state.fileErrors.length > 0 ? (
            <ul className="mt-2 space-y-1 text-xs text-red-700 list-disc pl-5">
              {state.fileErrors.map((item) => (
                <li key={`${item.entryPath}:${item.message}`}>
                  {t('dlgPlanUploadProgressFileError', {
                    ns: NS,
                    file: item.entryPath,
                    error: item.message,
                  })}
                </li>
              ))}
            </ul>
          ) : null}
          {!isTerminal ? (
            <p className="text-xs text-gray-500">{t('dlgPlanUploadProgressCloseHint', { ns: NS })}</p>
          ) : null}
        </section>
        <footer className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
          <button type="button" className={closeButtonClass} onClick={onClose}>
            {t('btnPlanUploadProgressClose', { ns: NS })}
          </button>
        </footer>
      </div>
    </U2000BasicDialog>
  );
};
